import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, RefreshCw, Trash2, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (iso) => {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

/**
 * Blocked permission attempts, grouped by role + permission, with a
 * one-click "Grant" that ticks the key in the matrix draft.
 */
export const RefusalLogPanel = ({ onGrant }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/permission-refusals/summary?days=30`);
      setRows(r.data || []);
    } catch (e) {
      // Non-admins simply don't see this panel's data.
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clear = async () => {
    try {
      await axios.delete(`${API}/permission-refusals`);
      setRows([]);
      toast.success(t('auto.misc2.refusalLogCleared'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not clear the log');
    }
  };

  return (
    <Card className="mt-6" data-testid="refusal-log-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-500" />
          {t('auto.misc2.blockedAttemptsLast30Days')}
        </CardTitle>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={load}
            disabled={loading} data-testid="refusal-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {t('auto.misc2.refresh')}
          </Button>
          {rows.length > 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-7 text-red-600"
              onClick={clear} data-testid="refusal-clear-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> {t('auto.misc2.clear')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="refusal-empty">
            Nothing blocked recently. When someone is refused an action, it shows up here with
            the exact permission they were missing.
          </p>
        ) : (
          <div className="space-y-2" data-testid="refusal-rows">
            {rows.map((r) => (
              <div key={`${r.role}-${r.permission}`}
                className="flex items-start justify-between gap-3 rounded-md border border-slate-200 p-2.5"
                data-testid={`refusal-row-${r.role}-${r.permission}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 break-words">
                    {r.label}
                    <span className="ml-2 font-mono text-[11px] text-slate-400">{r.permission}</span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                    <Badge variant="outline" className="capitalize">{r.role || 'unknown role'}</Badge>
                    <span>{r.count} {r.count === 1 ? 'attempt' : 'attempts'}</span>
                    <span>· last {timeAgo(r.last_at)}</span>
                    {r.users?.length > 0 && <span>· {r.users.join(', ')}</span>}
                  </div>
                  {r.paths?.length > 0 && (
                    <div className="mt-1 font-mono text-[10px] text-slate-400 break-all">
                      {r.paths.slice(0, 2).join('  ')}
                    </div>
                  )}
                </div>
                {r.grantable ? (
                  <Button size="sm" variant="outline" className="shrink-0 text-xs h-7"
                    onClick={() => onGrant?.(r.role, r.permission)}
                    data-testid={`refusal-grant-${r.role}-${r.permission}`}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> {t('auto.misc2.grant')}
                  </Button>
                ) : (
                  <Badge variant="secondary" className="shrink-0 text-[10px]">{t('auto.misc2.adminsOnly')}</Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RefusalLogPanel;
